import {
  DEFAULT_MODEL_VIEWER_ASPECT_RATIO,
  MODEL_VIEWER_ASPECT_RATIO_AUTO,
  ModelViewerAspectRatioSelection,
  ModelViewerAspectRatioValue,
  modelViewerAspectRatioValues
} from '../../modelViewerAspectRatioConstants';

interface SizeLike {
  width: number;
  height: number;
}

/**
 * Picks the preset whose ratio is perceptually closest to the measured box.
 * Distance is |ln(a / b)| rather than |a - b|: the preset list is mirrored
 * (16:9 / 9:16, 4:3 / 3:4, …), and only log distance is symmetric under
 * inversion — a raw difference biases every match toward the wide presets.
 */
export function findNearestModelViewerAspectRatio(
  size: SizeLike | null | undefined
): ModelViewerAspectRatioValue {
  const width = size?.width ?? 0;
  const height = size?.height ?? 0;

  if (!(width > 0) || !(height > 0)) {
    return DEFAULT_MODEL_VIEWER_ASPECT_RATIO;
  }

  const target = Math.log(width / height);
  let nearest = DEFAULT_MODEL_VIEWER_ASPECT_RATIO;
  let nearestDistance = Infinity;

  for (const value of Object.keys(
    modelViewerAspectRatioValues
  ) as ModelViewerAspectRatioValue[]) {
    const preset = modelViewerAspectRatioValues[value];
    const distance = Math.abs(Math.log(preset.width / preset.height) - target);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = value;
    }
  }

  return nearest;
}

/**
 * Resolves the UI selection down to a real preset. In "auto" the frame is
 * unconstrained on screen, but every outgoing request (and the screenshot
 * crop) still uses a concrete preset — the nearest one to what the user
 * actually sees — so the backend contract is unchanged by auto mode.
 */
export function resolveModelViewerAspectRatio(
  selection: ModelViewerAspectRatioSelection,
  size: SizeLike | null | undefined
): ModelViewerAspectRatioValue {
  return selection === MODEL_VIEWER_ASPECT_RATIO_AUTO
    ? findNearestModelViewerAspectRatio(size)
    : selection;
}
